import { prefersReducedMotion, isLowPowerDevice } from '@theme/utilities';

class WanedcReveal {
  static selector = '[data-wanedc-reveal]';
  #observer = null;
  #observedElements = new Set();

  constructor() {
    if (prefersReducedMotion() || isLowPowerDevice() || !window.IntersectionObserver) {
      document.querySelectorAll(WanedcReveal.selector).forEach((el) => {
        el.classList.add('is-visible');
        const shimmer = el.matches('.wanedc-card-shimmer--active') ? el : el.querySelector('.wanedc-card-shimmer--active');
        if (shimmer) shimmer.classList.add('wanedc-card-shimmer--shimmer-visible');
      });
      return;
    }

    this.#observer = new IntersectionObserver(this.#onIntersect, {
      root: null,
      rootMargin: '-2% 0px',
      threshold: 0.01,
    });

    this.observeAll();

    document.addEventListener('shopify:section:load', this.#onSectionLoad);
    document.addEventListener('shopify:section:reorder', this.#onSectionLoad);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  observeAll = () => {
    const els = document.querySelectorAll(WanedcReveal.selector);
    els.forEach((el, index) => {
      if (this.#observedElements.has(el) || el.classList.contains('is-visible')) return;
      // stagger: (index % 6) * 0.05s, mimic m-edc 0.05s per card
      const staggerDelay = (index % 6) * 0.05;
      el.style.setProperty('--wanedc-reveal-delay', `${staggerDelay}s`);
      this.#observer.observe(el);
      this.#observedElements.add(el);
    });
  };

  #onIntersect = (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      el.classList.add('is-visible');
      this.#observer.unobserve(el);
      this.#observedElements.delete(el);

      // 700ms淡入后荧光二次显示通过CSS transition-delay处理，此处仅清理will-change
      const onTransitionEnd = (e) => {
        if (e.propertyName === 'transform' || e.propertyName === 'opacity') {
          el.style.willChange = 'auto';
          el.removeEventListener('transitionend', onTransitionEnd);
        }
      };
      el.addEventListener('transitionend', onTransitionEnd);

      // 兼容旧浏览器：若无transition，仍需确保荧光可见
      const shimmer = el.matches('.wanedc-card-shimmer--active') ? el : el.querySelector('.wanedc-card-shimmer--active');
      if (shimmer) {
        // CSS已通过 [data-wanedc-reveal].is-visible .wanedc-card-shimmer--active::before 的 transition-delay 控制
        // 这里不需额外JS，保留类以备扩展
      }
    });
  };

  #onSectionLoad = (event) => {
    const root = event.target;
    if (!root) return;
    const newEls = root.querySelectorAll ? root.querySelectorAll(WanedcReveal.selector) : [];
    newEls.forEach((el) => {
      if (el.classList.contains('is-visible')) return;
      if (this.#observer && !this.#observedElements.has(el)) {
        // 延迟观察以确保DOM稳定
        requestAnimationFrame(() => {
          this.#observer.observe(el);
          this.#observedElements.add(el);
        });
      } else {
        el.classList.add('is-visible');
      }
    });
    // 编辑器下立即显示
    if (window.Shopify && window.Shopify.designMode) {
      newEls.forEach((el) => el.classList.add('is-visible'));
    }
  };

  #onBlockSelect = (event) => {
    const block = event.target;
    if (!block) return;
    const revealEl = block.querySelector?.(WanedcReveal.selector) || block.closest?.(WanedcReveal.selector);
    if (revealEl) {
      revealEl.classList.add('is-visible');
    }
  };

  disconnect() {
    this.#observer?.disconnect();
    this.#observedElements.clear();
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    document.removeEventListener('shopify:section:reorder', this.#onSectionLoad);
  }
}

let instance = null;

function initWanedcReveal() {
  if (instance) instance.disconnect();
  instance = new WanedcReveal();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWanedcReveal);
} else {
  initWanedcReveal();
}

export { WanedcReveal, initWanedcReveal };
